import React, { useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment-jalaali';
import { BRAND, HAIRLINE } from './assessment/AssessmentUI';

interface PersianDatePickerProps {
  /** مقدار به‌شکل jYYYY/jMM/jDD (شمسی) — همان چیزی که نمایش داده و ذخیره می‌شود */
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  error?: boolean;
}

const MONTH_NAMES = [
  'فروردین', 'اردیبهشت', 'خرداد', 'تیر', 'مرداد', 'شهریور',
  'مهر', 'آبان', 'آذر', 'دی', 'بهمن', 'اسفند',
];
const DAY_NAMES = ['ش', 'ی', 'د', 'س', 'چ', 'پ', 'ج'];

const parseJalali = (value?: string) => {
  if (!value) return null;
  const parsed = moment(value.trim(), 'jYYYY/jMM/jDD', true);
  return parsed.isValid() ? parsed : null;
};

const PersianDatePicker: React.FC<PersianDatePickerProps> = ({
  value,
  onChange,
  placeholder = 'انتخاب تاریخ',
  className = '',
  error,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedMoment = useMemo(() => parseJalali(value), [value]);
  const [viewDate, setViewDate] = useState(() => selectedMoment || moment());
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (selectedMoment) setViewDate(selectedMoment);
  }, [selectedMoment]);

  useEffect(() => {
    if (!isOpen) return;
    const onClickOutside = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, [isOpen]);

  const currentJYear = moment().jYear();
  const yearOptions = useMemo(
    () => Array.from({ length: 90 }, (_, i) => currentJYear - i),
    [currentJYear],
  );

  const days = useMemo(() => {
    const startOfMonth = viewDate.clone().startOf('jMonth');
    const endOfMonth = viewDate.clone().endOf('jMonth');
    const startOfGrid = startOfMonth.clone().startOf('week');
    const endOfGrid = endOfMonth.clone().endOf('week');
    const out: moment.Moment[] = [];
    const cursor = startOfGrid.clone();
    while (cursor.isSameOrBefore(endOfGrid, 'day')) {
      out.push(cursor.clone());
      cursor.add(1, 'day');
    }
    return out;
  }, [viewDate]);

  const handleSelect = (day: moment.Moment) => {
    onChange(day.format('jYYYY/jMM/jDD'));
    setIsOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <input
        type="text"
        readOnly
        value={value || ''}
        onClick={() => setIsOpen((v) => !v)}
        placeholder={placeholder}
        className="w-full cursor-pointer rounded-xl border bg-white px-3 py-2.5 text-[13px] text-slate-800 outline-none transition focus:border-[#9DB0E4]"
        style={{ borderColor: error ? '#FCA5A5' : HAIRLINE }}
      />
      {isOpen ? (
        <div
          className="absolute right-0 z-50 mt-2 w-72 overflow-hidden rounded-2xl border bg-white shadow-[0_24px_50px_-24px_rgba(15,23,42,0.35)]"
          style={{ borderColor: HAIRLINE }}
        >
          <div className="flex items-center justify-between gap-1 border-b p-2.5" style={{ borderColor: HAIRLINE }}>
            <button
              type="button"
              onClick={() => setViewDate(viewDate.clone().subtract(1, 'jMonth'))}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-1">
              <select
                value={viewDate.jMonth()}
                onChange={(e) => setViewDate(viewDate.clone().jMonth(Number(e.target.value)))}
                className="rounded-lg border-none bg-slate-50 px-1.5 py-1 text-[12px] font-bold text-slate-700 outline-none"
              >
                {MONTH_NAMES.map((m, i) => (
                  <option key={m} value={i}>
                    {m}
                  </option>
                ))}
              </select>
              <select
                value={viewDate.jYear()}
                onChange={(e) => setViewDate(viewDate.clone().jYear(Number(e.target.value)))}
                className="rounded-lg border-none bg-slate-50 px-1.5 py-1 text-[12px] font-bold text-slate-700 outline-none"
              >
                {yearOptions.map((y) => (
                  <option key={y} value={y}>
                    {y.toLocaleString('fa-IR').replace(/,/g, '')}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="button"
              onClick={() => setViewDate(viewDate.clone().add(1, 'jMonth'))}
              className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-100"
            >
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-0.5 px-2 pt-2">
            {DAY_NAMES.map((d) => (
              <div key={d} className="py-1 text-center text-[10.5px] font-bold text-slate-400">
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5 px-2 pb-2.5">
            {days.map((day, i) => {
              const isCurrentMonth = day.jMonth() === viewDate.jMonth();
              const isSelected = !!selectedMoment && day.isSame(selectedMoment, 'day');
              const isToday = day.isSame(moment(), 'day');
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => handleSelect(day)}
                  className="rounded-lg py-1.5 text-[12px] transition"
                  style={{
                    color: isSelected ? 'white' : isCurrentMonth ? '#1e293b' : '#cbd5e1',
                    backgroundColor: isSelected ? BRAND : isToday ? `${BRAND}14` : 'transparent',
                    fontWeight: isSelected || isToday ? 700 : 400,
                  }}
                >
                  {day.jDate().toLocaleString('fa-IR')}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between gap-2 border-t p-2.5" style={{ borderColor: HAIRLINE }}>
            <button
              type="button"
              onClick={() => {
                onChange('');
                setIsOpen(false);
              }}
              className="rounded-lg border px-3 py-1.5 text-[11px] font-bold text-slate-500"
              style={{ borderColor: HAIRLINE }}
            >
              پاک‌کردن
            </button>
            <button
              type="button"
              onClick={() => handleSelect(moment())}
              className="rounded-lg px-3 py-1.5 text-[11px] font-bold text-white"
              style={{ backgroundColor: BRAND }}
            >
              امروز
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PersianDatePicker;
